import { useState, useCallback, useRef, useMemo, useEffect, type JSX } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  type ColDef,
  type GridApi,
  type GridReadyEvent,
  type ICellRendererParams,
  type IGetRowsParams,
  type ValueFormatterParams,
  type ValueGetterParams,
} from 'ag-grid-community';
import { Search, Plus, Wallet, X } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';
import { extractEnvelope } from '../../lib/extractEnvelope';
import type { PostgrestError } from '@supabase/supabase-js';
import { escapeOrIlikeTerm } from '../../lib/postgrestEscape';
import { SELECTION_COLUMN_DEF, emptyFormatter } from '../../lib/gridUtils';
import { reportDatasourceFailure } from '../../lib/agGridErrorTracker';
import { useSettingsConfig } from '../../hooks/useSettingsConfig';
import ArbiSelect from '../../components/ui/ArbiSelect';
import ExportButton from '../../components/ui/ExportButton';

ModuleRegistry.registerModules([AllCommunityModule]);

// Loose RPC caller — transition_payout_status is a held-batch fn not in the generated
// types until the Push-1 regen.
type GenericSupabaseRpc = (
  name: string,
  args: Record<string, unknown>
) => PromiseLike<{ data: unknown; error: PostgrestError | null }>;
const callRpc = supabase.rpc.bind(supabase) as unknown as GenericSupabaseRpc;

// ── Local types ─────────────────────────────────────────────────────────────

type PayoutStatus = 'pending' | 'received' | 'reconciled';
type PayoutStatusFilter = 'all' | PayoutStatus;

interface PayoutRow {
  id: string;
  channel: string | null;
  period_start: string | null;
  period_end: string | null;
  gross_amount: number | null;
  fees: number | null;
  net_amount: number | null;
  status: string;
  notes: string | null;
  received_at: string | null;
}

interface AddPayoutForm {
  channel: string;
  period_start: string;
  period_end: string;
  gross_amount: string;
  fees: string;
  net_amount: string;
  status: PayoutStatus;
  notes: string;
}

// ── Formatting ──────────────────────────────────────────────────────────────

function fmtCurrency(v: number | string | null | undefined): string {
  if (v === null || v === undefined) return '\u2014';
  return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function relativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '\u2014';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function formatPeriod(start: string | null | undefined, end: string | null | undefined): string {
  if (!start || !end) return '\u2014';
  const s = new Date(start);
  const e = new Date(end);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const yearOpts: Intl.DateTimeFormatOptions = { ...opts, year: 'numeric' };
  return `${s.toLocaleDateString('en-US', opts)} \u2013 ${e.toLocaleDateString('en-US', yearOpts)}`;
}

const CHANNEL_COLORS: Record<string, string> = {
  amazon_fba: 'bg-info/15 text-info',
  amazon_fbm: 'bg-info/15 text-info',
  ebay: 'bg-warning/15 text-warning',
  walmart_self: 'bg-accent/15 text-accent',
  walmart_wfs: 'bg-accent/15 text-accent',
  shopify: 'bg-success/15 text-success',
};

const CHANNEL_LABELS: Record<string, string> = {
  amazon_fba: 'FBA',
  amazon_fbm: 'FBM',
  ebay: 'eBay',
  walmart_self: 'Walmart',
  walmart_wfs: 'WFS',
  shopify: 'Shopify',
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-surface-3 text-ink-secondary',
  received: 'bg-info/15 text-info',
  reconciled: 'bg-success/15 text-success',
};

const STATUS_TABS: ReadonlyArray<{ key: PayoutStatusFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'received', label: 'Received' },
  { key: 'reconciled', label: 'Reconciled' },
] as const;

// Fallback when fulfillment_path_config hasn't loaded yet (60s TTL cache miss).
// Day 60b — operator-input options derived from activePaths at render time.
// CHANNEL_LABELS / CHANNEL_COLORS above stay full (display-only — historical
// channel data on existing rows must render even after the path is deactivated).
const CHANNEL_OPTIONS_FALLBACK = [
  { value: '', label: 'All Channels' },
  { value: 'amazon_fba', label: 'Amazon FBA' },
  { value: 'amazon_fbm', label: 'Amazon FBM' },
  { value: 'ebay', label: 'eBay' },
  { value: 'walmart_self', label: 'Walmart Self' },
  { value: 'walmart_wfs', label: 'Walmart WFS' },
  { value: 'shopify', label: 'Shopify' },
];

// ── Add Payout Modal ────────────────────────────────────────────────────────

interface AddPayoutModalProps {
  onClose: () => void;
  onSaved: () => void;
}

function AddPayoutModal({ onClose, onSaved }: AddPayoutModalProps): JSX.Element {
  const { activePaths } = useSettingsConfig();
  const [form, setForm] = useState<AddPayoutForm>({
    channel: '',
    period_start: '',
    period_end: '',
    gross_amount: '',
    fees: '',
    net_amount: '',
    status: 'pending',
    notes: '',
  });
  const [saving, setSaving] = useState<boolean>(false);

  // Modal Channel dropdown — drop the 'All Channels' sentinel and emit only
  // active paths. Falls back to the static list when settings haven't loaded.
  const modalChannelOptions = useMemo(() => {
    if (!activePaths || activePaths.length === 0) {
      return CHANNEL_OPTIONS_FALLBACK.filter((o) => o.value);
    }
    return activePaths.map((p) => ({ value: p.path_key, label: p.display_name }));
  }, [activePaths]);

  // Auto-calc net
  const gross = parseFloat(form.gross_amount) || 0;
  const fees = parseFloat(form.fees) || 0;
  const autoNet = (gross - fees).toFixed(2);

  const handleSave = async (): Promise<void> => {
    if (!form.channel || !form.period_start || !form.period_end) return;
    setSaving(true);
    const payload = {
      channel: form.channel,
      period_start: form.period_start,
      period_end: form.period_end,
      gross_amount: form.gross_amount ? parseFloat(form.gross_amount) : null,
      fees: form.fees ? parseFloat(form.fees) : null,
      net_amount: form.net_amount ? parseFloat(form.net_amount) : parseFloat(autoNet) || null,
      status: form.status,
      notes: form.notes.trim() || null,
    };
    const { error } = await supabase.from('payouts').insert(payload as never);
    setSaving(false);
    if (!error) {
      onSaved();
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 'var(--z-modal, 200)' }}
    >
      <button
        type="button"
        aria-label="Close dialog"
        onClick={onClose}
        className="absolute inset-0 bg-surface-0/80 cursor-default"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Add Payout"
        className="relative bg-surface-1 border border-border rounded-xl shadow-xl w-full max-w-md mx-4"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h2 className="text-sm font-semibold text-ink-bright">Add Payout</h2>
          <button
            onClick={onClose}
            className="arbi-btn-ghost p-1 rounded cursor-pointer"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label
              htmlFor="payout-channel"
              className="text-xs font-medium text-ink-secondary block mb-1"
            >
              Channel <span className="text-danger">*</span>
            </label>
            <div id="payout-channel">
              <ArbiSelect
                value={form.channel}
                options={modalChannelOptions}
                onChange={(v) => setForm((f) => ({ ...f, channel: String(v) }))}
                placeholder="Select channel..."
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="payout-start"
                className="text-xs font-medium text-ink-secondary block mb-1"
              >
                Period Start <span className="text-danger">*</span>
              </label>
              <input
                id="payout-start"
                value={form.period_start}
                onChange={(e) => setForm((f) => ({ ...f, period_start: e.target.value }))}
                className="arbi-input px-3 py-1.5 text-xs w-full font-mono"
                type="date"
              />
            </div>
            <div>
              <label
                htmlFor="payout-end"
                className="text-xs font-medium text-ink-secondary block mb-1"
              >
                Period End <span className="text-danger">*</span>
              </label>
              <input
                id="payout-end"
                value={form.period_end}
                onChange={(e) => setForm((f) => ({ ...f, period_end: e.target.value }))}
                className="arbi-input px-3 py-1.5 text-xs w-full font-mono"
                type="date"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label
                htmlFor="payout-gross"
                className="text-xs font-medium text-ink-secondary block mb-1"
              >
                Gross
              </label>
              <input
                id="payout-gross"
                value={form.gross_amount}
                onChange={(e) =>
                  setForm((f) => ({ ...f, gross_amount: e.target.value.replace(/[^0-9.]/g, '') }))
                }
                className="arbi-input px-3 py-1.5 text-xs w-full font-mono"
                placeholder="0.00"
                type="text"
                inputMode="decimal"
              />
            </div>
            <div>
              <label
                htmlFor="payout-fees"
                className="text-xs font-medium text-ink-secondary block mb-1"
              >
                Fees
              </label>
              <input
                id="payout-fees"
                value={form.fees}
                onChange={(e) =>
                  setForm((f) => ({ ...f, fees: e.target.value.replace(/[^0-9.]/g, '') }))
                }
                className="arbi-input px-3 py-1.5 text-xs w-full font-mono"
                placeholder="0.00"
                type="text"
                inputMode="decimal"
              />
            </div>
            <div>
              <label
                htmlFor="payout-net"
                className="text-xs font-medium text-ink-secondary block mb-1"
              >
                Net
              </label>
              <input
                id="payout-net"
                value={form.net_amount || autoNet}
                onChange={(e) =>
                  setForm((f) => ({ ...f, net_amount: e.target.value.replace(/[^0-9.-]/g, '') }))
                }
                className="arbi-input px-3 py-1.5 text-xs w-full font-mono"
                placeholder="0.00"
                type="text"
                inputMode="decimal"
              />
            </div>
          </div>
          <div>
            <label
              htmlFor="payout-status"
              className="text-xs font-medium text-ink-secondary block mb-1"
            >
              Status
            </label>
            <div id="payout-status">
              <ArbiSelect
                value={form.status}
                options={[
                  { value: 'pending', label: 'Pending' },
                  { value: 'received', label: 'Received' },
                  { value: 'reconciled', label: 'Reconciled' },
                ]}
                onChange={(v) => setForm((f) => ({ ...f, status: String(v) as PayoutStatus }))}
              />
            </div>
          </div>
          <div>
            <label
              htmlFor="payout-notes"
              className="text-xs font-medium text-ink-secondary block mb-1"
            >
              Notes
            </label>
            <textarea
              id="payout-notes"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              className="arbi-input px-3 py-1.5 text-xs w-full h-16 resize-none"
              placeholder="Optional notes..."
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border">
          <button onClick={onClose} className="arbi-btn-ghost text-xs px-3 py-1.5 cursor-pointer">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !form.channel || !form.period_start || !form.period_end}
            className="arbi-btn-primary text-xs px-3 py-1.5 cursor-pointer disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Add Payout'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ───────────────────────────────────────────────────────────────

export default function FinancePayouts(): JSX.Element {
  const { activePaths } = useSettingsConfig();
  const gridRef = useRef<AgGridReact<PayoutRow> | null>(null);
  const gridApiRef = useRef<GridApi<PayoutRow> | null>(null);

  const [searchText, setSearchText] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<PayoutStatusFilter>('all');
  const [channelFilter, setChannelFilter] = useState<string>('');
  const [totalCount, setTotalCount] = useState<number>(0);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  // Filter dropdown — keep the 'All Channels' sentinel as first option and
  // emit only active paths after it.
  const filterChannelOptions = useMemo(() => {
    if (!activePaths || activePaths.length === 0) return CHANNEL_OPTIONS_FALLBACK;
    return [
      { value: '', label: 'All Channels' },
      ...activePaths.map((p) => ({ value: p.path_key, label: p.display_name })),
    ];
  }, [activePaths]);

  const stateRef = useRef<{
    searchText: string;
    statusFilter: PayoutStatusFilter;
    channelFilter: string;
  }>({ searchText, statusFilter, channelFilter });
  stateRef.current = { searchText, statusFilter, channelFilter };

  const getRowsFn = useCallback(async (params: IGetRowsParams): Promise<void> => {
    const { startRow, endRow, sortModel, successCallback, failCallback } = params;
    const { searchText: st, statusFilter: sf, channelFilter: cf } = stateRef.current;
    try {
      let query = supabase.from('payouts').select('*', { count: 'exact' });

      if (sf !== 'all') query = query.eq('status', sf);
      if (cf) query = query.eq('channel', cf);
      if (st.trim()) {
        // Day 76 P5.1 Phase 9A — escapeOrIlikeTerm adoption (Day 73 P4.1)
        const term = escapeOrIlikeTerm(st.trim());
        query = query.or(`channel.ilike.${term},notes.ilike.${term}`);
      }

      if (sortModel.length > 0) {
        const s = sortModel[0];
        query = query.order(s.colId, { ascending: s.sort === 'asc', nullsFirst: false });
      } else {
        query = query.order('period_end', { ascending: false });
      }

      query = query.range(startRow, endRow - 1);
      const { data, count, error } = await query;
      if (error) throw error;
      if (startRow === 0) setTotalCount(count || 0);
      successCallback((data as unknown as PayoutRow[]) || [], count || 0);
    } catch (e) {
      reportDatasourceFailure(
        'Finance — Payouts',
        { rpcName: 'payouts', params: { startRow, endRow } },
        e,
        () => failCallback()
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onGridReady = useCallback(
    (params: GridReadyEvent<PayoutRow>): void => {
      gridApiRef.current = params.api;
      params.api.setGridOption('datasource', { getRows: getRowsFn });
    },
    [getRowsFn]
  );

  useEffect(() => {
    if (gridApiRef.current) gridApiRef.current.setGridOption('datasource', { getRows: getRowsFn });
  }, [searchText, statusFilter, channelFilter, getRowsFn]);

  const refreshGrid = useCallback((): void => {
    if (gridApiRef.current) gridApiRef.current.setGridOption('datasource', { getRows: getRowsFn });
  }, [getRowsFn]);

  const columnDefs = useMemo<ColDef<PayoutRow>[]>(
    () =>
      [
        {
          headerName: 'Channel',
          field: 'channel',
          width: 130,
          cellRenderer: (p: ICellRendererParams<PayoutRow>) => {
            const ch = p.data?.channel;
            if (!p.data || !ch) return null;
            return (
              <span
                className={clsx(
                  'px-1.5 py-0.5 rounded text-2xs font-medium',
                  CHANNEL_COLORS[ch] || 'bg-surface-3 text-ink-muted'
                )}
              >
                {CHANNEL_LABELS[ch] || ch}
              </span>
            );
          },
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Period',
          colId: 'period',
          flex: 1,
          minWidth: 180,
          valueGetter: (p: ValueGetterParams<PayoutRow>) =>
            p.data ? formatPeriod(p.data.period_start, p.data.period_end) : '\u2014',
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            fontFamily: "'JetBrains Mono', monospace",
          },
        },
        {
          headerName: 'Gross',
          field: 'gross_amount',
          width: 120,
          valueFormatter: (p: ValueFormatterParams<PayoutRow>) =>
            fmtCurrency(p.value as number | null),
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            fontFamily: "'JetBrains Mono', monospace",
          },
          headerClass: 'ag-right-aligned-header',
        },
        {
          headerName: 'Fees',
          field: 'fees',
          width: 110,
          valueFormatter: (p: ValueFormatterParams<PayoutRow>) =>
            fmtCurrency(p.value as number | null),
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            fontFamily: "'JetBrains Mono', monospace",
          },
          headerClass: 'ag-right-aligned-header',
        },
        {
          headerName: 'Net',
          field: 'net_amount',
          width: 120,
          valueFormatter: (p: ValueFormatterParams<PayoutRow>) =>
            fmtCurrency(p.value as number | null),
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            fontFamily: "'JetBrains Mono', monospace",
            color: 'var(--success)',
          },
          headerClass: 'ag-right-aligned-header',
        },
        {
          headerName: 'Status',
          field: 'status',
          width: 110,
          cellRenderer: (p: ICellRendererParams<PayoutRow>) => {
            const status = p.data?.status;
            return p.data && status ? (
              <span
                className={clsx(
                  'px-1.5 py-0.5 rounded text-2xs font-medium',
                  STATUS_COLORS[status]
                )}
              >
                {status}
              </span>
            ) : null;
          },
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        {
          headerName: 'Received',
          field: 'received_at',
          width: 120,
          valueFormatter: (p: ValueFormatterParams<PayoutRow>) =>
            relativeTime(p.value as string | null),
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            fontFamily: "'JetBrains Mono', monospace",
            color: 'var(--ink-muted)',
          },
        },
        {
          headerName: 'Actions',
          width: 130,
          sortable: false,
          // denom-F6: the basic pending→received transition (guarded by
          // transition_payout_status). 'reconciled' + settlement-import stay post-launch.
          cellRenderer: (p: ICellRendererParams<PayoutRow>) =>
            p.data?.status === 'pending' ? (
              <button
                onClick={() => {
                  const row = p.data!;
                  void (async () => {
                    const env = extractEnvelope<{ status: string }>(
                      await callRpc('transition_payout_status', {
                        p_payout_id: row.id,
                        p_expected_status: row.status,
                        p_new_status: 'received',
                      })
                    );
                    if (env.isOk) gridApiRef.current?.refreshInfiniteCache();
                  })();
                }}
                className="arbi-btn-ghost text-2xs px-2 py-0.5 cursor-pointer"
              >
                Mark Received
              </button>
            ) : null,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
      ] as ColDef<PayoutRow>[],
    []
  );

  const defaultColDef = useMemo<ColDef<PayoutRow>>(
    () => ({
      sortable: true,
      resizable: true,
      sortingOrder: ['asc', 'desc', null],
      unSortIcon: true,
      headerClass: 'ag-left-aligned-header',
      cellStyle: { textAlign: 'left' },
      valueFormatter: emptyFormatter,
    }),
    []
  );

  const gridTheme = useMemo(
    () =>
      themeQuartz.withParams({
        backgroundColor: 'var(--surface-0)',
        headerBackgroundColor: 'var(--surface-1)',
        headerTextColor: 'var(--ink-muted)',
        foregroundColor: 'var(--ink-primary)',
        borderColor: 'var(--border)',
        rowHoverColor: 'var(--surface-3)',
        headerFontSize: 10,
        fontSize: 12,
        spacing: 4,
      }),
    []
  );

  return (
    <div className="flex flex-col h-full -m-4">
      {/* Toolbar */}
      <div className="flex-shrink-0 px-4 py-2 bg-surface-1 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h1 className="text-base font-semibold text-ink-bright">Payouts</h1>
            {totalCount > 0 && (
              <span className="text-xs text-ink-muted">{totalCount.toLocaleString()}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <ExportButton gridApiRef={gridApiRef} fileName="arbi-payouts" />
            <button
              onClick={() => setModalOpen(true)}
              className="arbi-btn-primary text-xs px-3 py-1 flex items-center gap-1 cursor-pointer"
            >
              <Plus size={12} /> Add Payout
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-surface-2 rounded-md p-0.5 gap-0.5 border border-border-subtle">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setStatusFilter(tab.key)}
                aria-pressed={statusFilter === tab.key}
                className={clsx(
                  'px-2 py-1 rounded text-2xs font-medium whitespace-nowrap transition-colors cursor-pointer',
                  statusFilter === tab.key
                    ? 'bg-accent/15 text-accent'
                    : 'text-ink-muted hover:text-ink-secondary'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <ArbiSelect
            value={channelFilter}
            options={filterChannelOptions}
            onChange={(v) => setChannelFilter(String(v))}
            placeholder="Channel..."
            className="w-40"
          />
          <div className="relative w-60 ml-auto">
            <Search
              size={12}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted"
            />
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search payouts..."
              aria-label="Search payouts"
              className="arbi-input pl-8 pr-3 py-1 text-xs w-full"
            />
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="flex-1 p-2" style={{ minHeight: 0 }}>
        <AgGridReact
          ref={gridRef}
          theme={gridTheme}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowModelType="infinite"
          cacheBlockSize={100}
          maxBlocksInCache={50}
          selectionColumnDef={SELECTION_COLUMN_DEF}
          rowSelection={{
            mode: 'multiRow',
            enableClickSelection: false,
            checkboxes: true,
            headerCheckbox: false,
          }}
          onGridReady={onGridReady}
          animateRows={false}
          headerHeight={36}
          rowHeight={42}
          suppressCellFocus={true}
          suppressHorizontalScroll={true}
          noRowsOverlayComponent={() => (
            <div className="flex flex-col items-center justify-center h-full">
              <Wallet size={48} className="text-ink-muted mb-3" />
              <p className="text-sm text-ink-muted">
                {statusFilter !== 'all' || searchText || channelFilter
                  ? 'No payouts match your filters'
                  : 'No payouts yet'}
              </p>
            </div>
          )}
        />
      </div>

      {/* Modal */}
      {modalOpen && <AddPayoutModal onClose={() => setModalOpen(false)} onSaved={refreshGrid} />}
    </div>
  );
}
